import {
  noop
} from "./chunk-23I7TQOH.mjs";
import {
  isArray,
  isNumber,
  isObject,
  isString,
  isUndefined
} from "./chunk-QWVYBC6R.mjs";

// src/react.tsx
import * as React from "react";
function createContext2({
  name,
  defaultValue,
  errorMessage = "useContext: `context` is undefined. Seems you forgot to wrap component within the Provider",
  strict = true
} = {}) {
  const Context = React.createContext(defaultValue);
  Context.displayName = name;
  const useContext2 = () => {
    const context = React.useContext(Context);
    if (!context && strict) {
      const error = new Error(errorMessage);
      error.name = "ContextError";
      Error.captureStackTrace(error, useContext2);
      throw error;
    }
    return context;
  };
  return [Context.Provider, useContext2, Context];
}
var useSafeLayoutEffect = Boolean(globalThis.document) ? React.useLayoutEffect : React.useEffect;
function useUnmountEffect(callback) {
  return React.useEffect(() => () => callback(), []);
}
var useIsMounted = useMounted;
function useMounted({
  delay = 0,
  rerender = false
} = {}) {
  const mountedRef = React.useRef(false);
  const [mounted, setMounted] = React.useState(false);
  useSafeLayoutEffect(() => {
    mountedRef.current = true;
    let timeoutId = null;
    if (rerender) {
      if (delay > 0) {
        timeoutId = setTimeout(() => setMounted(true), delay);
      } else {
        setMounted(true);
      }
    }
    return () => {
      mountedRef.current = false;
      if (rerender) setMounted(false);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [delay, rerender]);
  return [React.useCallback(() => mountedRef.current, []), mounted];
}
function useIsSsr() {
  if (typeof React.useSyncExternalStore === "function")
    return React.useSyncExternalStore(
      () => noop,
      () => false,
      () => true
    );
  return false;
}
function getValidChildren(children) {
  return React.Children.toArray(children).filter(
    (child) => React.isValidElement(child)
  );
}
function isValidElement2(child) {
  return React.isValidElement(child) || isString(child) || isNumber(child);
}
function isSomeDisplayName(a, b) {
  if (isUndefined(a) || isUndefined(b)) return false;
  if (isArray(a)) {
    if (a.includes(b)) return true;
    if (!!b.displayName && a.includes(b.displayName)) return true;
    if (!!b.name && a.includes(b.name)) return true;
  } else {
    if (a === b) return true;
    if (!!a.displayName && !!b.displayName && a.displayName === b.displayName)
      return true;
    if (!!a.name && !!b.name && a.name === b.name) return true;
    if (!!a.displayName && !!b.name && a.displayName === b.name) return true;
    if (!!a.name && !!b.displayName && a.name === b.displayName) return true;
  }
  return false;
}
function isSomeElement(a, b) {
  var _a;
  if (isUndefined(a) || isUndefined(b)) return false;
  if (a === b) return true;
  if (!!a.__ui__ && !!b.__ui__ && a.__ui__ === b.__ui__) return true;
  if (isSomeDisplayName(a, b)) return true;
  a = (_a = a._payload) == null ? void 0 : _a.value;
  if (isUndefined(a)) return false;
  if (!!a.__ui__ && !!b.__ui__ && a.__ui__ === b.__ui__) return true;
  if (isSomeDisplayName(a, b)) return true;
  return false;
}
function findChild(children, ...types) {
  const child = children.find(
    (child2) => types.some((type) => isSomeElement(child2.type, type))
  );
  return child;
}
function findChildren(children, ...types) {
  const child = findChild(children, ...types);
  if (child) {
    return children.sort((a, b) => {
      if (types.some((type) => isSomeElement(a.type, type))) {
        return -1;
      } else if (types.some((type) => isSomeElement(b.type, type))) {
        return 1;
      } else {
        return 0;
      }
    });
  } else {
    return [void 0, ...children];
  }
}
function includesChildren(children, ...types) {
  return children.some((child) => {
    if (types.some((type) => isSomeElement(child.type, type))) return true;
    const children2 = getValidChildren(child.props.children);
    return children2.length ? includesChildren(children2, ...types) : false;
  });
}
function omitChildren(children, ...types) {
  return children.filter(
    (child) => types.every((type) => !isSomeElement(child.type, type))
  );
}
function pickChildren(children, ...types) {
  return children.filter(
    (child) => types.some((type) => isSomeElement(child.type, type))
  );
}
function cx(...classNames) {
  return classNames.filter(Boolean).join(" ");
}
function isRefObject(val) {
  return isObject(val) && "current" in val;
}
function getRef(element) {
  var _a, _b;
  let getter = (_a = Object.getOwnPropertyDescriptor(element.props, "ref")) == null ? void 0 : _a.get;
  if (getter && "isReactWarning" in getter && getter.isReactWarning)
    return element.ref;
  getter = (_b = Object.getOwnPropertyDescriptor(element, "ref")) == null ? void 0 : _b.get;
  if (getter && "isReactWarning" in getter && getter.isReactWarning)
    return element.props.ref;
  return element.props.ref || element.ref;
}
function assignRef(ref, value) {
  if (ref == null) return;
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  try {
    ref.current = value;
  } catch {
    throw new Error(`Cannot assign value '${value}' to ref '${ref}'`);
  }
}
function mergeRefs(...refs) {
  return function(node) {
    return refs.forEach((ref) => {
      assignRef(ref, node);
    });
  };
}
function useMergeRefs(...refs) {
  return React.useMemo(() => mergeRefs(...refs), [refs]);
}
function useCallbackRef(callback, deps = []) {
  const callbackRef = React.useRef(callback);
  React.useEffect(() => {
    callbackRef.current = callback;
  });
  return React.useCallback(
    (...args) => {
      var _a;
      return (_a = callbackRef.current) == null ? void 0 : _a.call(callbackRef, ...args);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps
  );
}
function useUpdateEffect(callback, deps) {
  const renderCycleRef = React.useRef(false);
  const effectCycleRef = React.useRef(false);
  React.useEffect(() => {
    const mounted = renderCycleRef.current;
    const run = mounted && effectCycleRef.current;
    if (run) return callback();
    effectCycleRef.current = true;
  }, deps);
  React.useEffect(() => {
    renderCycleRef.current = true;
    return () => {
      renderCycleRef.current = false;
    };
  }, []);
}
function useAsync(func, deps = []) {
  const [state, callback] = useAsyncFunc(func, deps, { loading: true });
  React.useEffect(() => {
    callback();
  }, [callback]);
  return state;
}
function useAsyncFunc(func, deps = [], initialState = { loading: false }) {
  const lastCallId = React.useRef(0);
  const [mounted] = useMounted();
  const [state, setState] = React.useState(initialState);
  const callback = React.useCallback(
    (...args) => {
      const callId = ++lastCallId.current;
      if (!state.loading)
        setState((prevState) => ({ ...prevState, loading: true }));
      return func(...args).then(
        (value) => {
          if (mounted() && callId === lastCallId.current)
            setState({ loading: false, value });
          return value;
        },
        (error) => {
          if (mounted() && callId === lastCallId.current)
            setState({ error, loading: false });
          return error;
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps
  );
  return [state, callback];
}
function useAsyncRetry(func, deps = []) {
  const [attempt, setAttempt] = React.useState(0);
  const state = useAsync(func, [...deps, attempt]);
  const stateLoading = state.loading;
  const retry = React.useCallback(() => {
    if (stateLoading) return;
    setAttempt((currentAttempt) => currentAttempt + 1);
  }, [...deps, stateLoading]);
  return { ...state, retry };
}
var createIdCounter = 0;
function createId(prefix) {
  return `${prefix}-${++createIdCounter}-${(/* @__PURE__ */ new Date()).getTime()}`;
}

export {
  createContext2 as createContext,
  useSafeLayoutEffect,
  useUnmountEffect,
  useIsMounted,
  useMounted,
  useIsSsr,
  getValidChildren,
  isValidElement2 as isValidElement,
  isSomeDisplayName,
  isSomeElement,
  findChild,
  findChildren,
  includesChildren,
  omitChildren,
  pickChildren,
  cx,
  isRefObject,
  getRef,
  assignRef,
  mergeRefs,
  useMergeRefs,
  useCallbackRef,
  useUpdateEffect,
  useAsync,
  useAsyncFunc,
  useAsyncRetry,
  createId
};
//# sourceMappingURL=chunk-EM4VFLLU.mjs.map